import Vue from 'vue'
import { EventBus } from 'chimera/all/plugins/eventbus'
import {
  getInitPromise,
  getStatsigClient,
} from 'chimera/all/plugins/tracking/statsig/install'
import { resolveVariant } from 'chimera/all/plugins/tracking/statsig/resolveVariant'
import { getRegisteredExperimentIds } from 'chimera/all/constants/experiments'

const OVERRIDES_STORAGE_KEY = 'chimera.experiment-overrides'

/**
 * @returns {object}
 */
function loadOverridesFromStorage() {
  try {
    const raw = localStorage.getItem(OVERRIDES_STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

/**
 * @param {object} override
 */
function saveOverridesToStorage(override) {
  try {
    localStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(override))
  } catch {}
}

const getDefaultState = () => ({
  assigned: [],
  override: loadOverridesFromStorage(),
})

export const state = getDefaultState

export const getters = {
  /**
   * @param {object} state
   * @returns {Array}
   */
  all: (state) => state.assigned,

  /**
   * @param {object} state
   * @returns {Function}
   */
  isAssigned: (state) => (id) => !!state.assigned.find((exp) => exp.id === id),

  /**
   * @param {object} state
   * @returns {Array}
   */
  codeList: (state) => state.assigned.map((exp) => exp.code),

  /**
   * @param {object} state
   * @returns {object}
   */
  getOverride: (state) => state.override,
}

export const mutations = {
  /**
   * @param {object} state
   * @param {object} experiment
   */
  addAssigned(state, experiment) {
    if (state.assigned.some((exp) => exp.id === experiment.id)) {
      return
    }
    state.assigned.push(experiment)
    EventBus.emitExperimentSetEvent(experiment)
  },

  /**
   * @param {object} state
   * @param {object} newState
   */
  reset(state, newState = getDefaultState()) {
    Object.assign(state, newState)
  },

  /**
   * @param {object} state
   * @param {object} payload
   * @param {string} payload.id
   * @param {number} payload.variant
   */
  addOverride(state, { id, variant }) {
    Vue.set(state.override, id, variant)
    saveOverridesToStorage(state.override)
  },

  /**
   * @param {object} state
   * @param {string} experimentId
   */
  removeOverride(state, experimentId) {
    Vue.delete(state.override, experimentId)
    saveOverridesToStorage(state.override)
  },
}

export const actions = {
  /**
   * Get or create an experiment assignment
   * @param {object} context
   * @param {object} context.state
   * @param {function(*): void} context.commit
   * @param {object} payload
   * @param {string} payload.id
   * @param {boolean} [payload.isVisualEditor] - Whether this is a Visual Editor experiment
   * @returns {Promise<Experiment|null>}
   */
  async get({ state, commit }, { id, isVisualEditor = false }) {
    const existing = state.assigned.find((exp) => exp.id === id)
    if (existing) {
      return existing
    }

    const experiment = await getExperiment(id, state.override, isVisualEditor)
    if (!experiment) {
      return null
    }

    // Track if a user is in experiment or using local override
    const shouldTrack = experiment.isUserInExperiment || experiment.isOverridden
    if (shouldTrack) {
      commit('addAssigned', experiment)
    }

    return experiment
  },

  /**
   * Load registered and overridden experiments so the DevToolbar can list them
   * before their exposure point.
   *
   * @param {object} context
   * @param {object} context.state
   * @param {function(*): void} context.commit
   * @param {string[]} [experimentIds]
   * @returns {Promise<void>}
   */
  async preload({ state, commit }, experimentIds = []) {
    await getInitPromise()

    const idsToPreload = [
      ...new Set([
        ...experimentIds,
        ...Object.keys(state.override),
        ...getRegisteredExperimentIds(process.env.concept),
      ]),
    ]

    for (const id of idsToPreload) {
      if (state.assigned.some((exp) => exp.id === id)) {
        continue
      }

      const experiment = await getExperiment(id, state.override, false)
      if (experiment) {
        commit('addAssigned', experiment)
      }
    }
  },
}

/**
 * @typedef {object} Experiment
 * @property {string} id
 * @property {number} variant
 * @property {number} variantCount
 * @property {string} code
 * @property {boolean} isRecognized
 * @property {boolean} isActive
 * @property {boolean} isUserInExperiment
 * @property {boolean} isOverridden
 * @property {boolean} isVisualEditor
 */

/**
 * Get experiment data from Statsig
 * @param {string} id - Experiment ID
 * @param {object} overrides - Local overrides from store
 * @param {boolean} isVisualEditor - Whether this is a Visual Editor experiment
 * @returns {Promise<Experiment|null>}
 */
async function getExperiment(id, overrides = {}, isVisualEditor = false) {
  if (!id) {
    EventBus.emitWarningAppErrorEvent(
      new Error('experiments/get called without an id'),
      { context: 'experiments/get' },
    )
    return null
  }

  await getInitPromise()

  const client = getStatsigClient()
  if (!client) {
    EventBus.emitWarningAppErrorEvent(
      new Error(
        `experiments/get: Statsig client unavailable when fetching "${id}"`,
      ),
      { context: 'experiments/get' },
    )
    return null
  }

  const experiment = client.getExperiment(id)
  const evaluation = experiment.__evaluation
  const reason = experiment.details?.reason || ''

  const variantCount = Number(experiment.get('groupCount', 2)) || 2

  const localOverride = overrides[id]
  const variant = resolveVariant(localOverride ?? experiment.groupName, id)

  return {
    id,
    variant,
    variantCount,
    code: `SG:${id}:${variant}:1`,
    isRecognized: !reason.includes('Unrecognized'),
    isActive: evaluation?.is_experiment_active ?? false,
    isUserInExperiment: evaluation?.is_user_in_experiment ?? false,
    isOverridden: localOverride !== undefined,
    isVisualEditor,
  }
}

export default {
  state,
  actions,
  mutations,
  getters,
}
